import React, { useState, useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import { Popup } from '@atlaskit/popup';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Pressable, xcss } from '@atlaskit/primitives';

import {
	confluenceLocalStorageInstance as localStorage,
	PERSISTED_KEYS_ON_SERVER,
} from '@confluence/storage-manager';
import { LoomScriptReadTimeButtonNudgeWrapper } from '@confluence/experiment-loom-recording-suggestions-on-confluence-pages-using-llms';

import { ReadTimeComponent } from './ReadTimeComponent';
import { ReadTimePopupWithErrorBoundary as ReadTimePopup } from './ReadTimePopup';
import type { ContentFeatures } from './featuresExtractor';

const triggerStyles = xcss({
	display: 'inline-flex',
	color: 'color.text',
	padding: 'space.0',
	borderRadius: 'border.radius.100',
	backgroundColor: 'color.background.neutral.subtle',
});

export const ReadTimePopupExperiment = ({
	contentId,
	isAbbreviatedReadTime,
	isDetailsPanel,
	showWordCount,
	features,
	featuresSkipExpand,
	wordCount,
	readTime,
	source,
	isEligibleForLoomAIScriptSection = false,
}: {
	contentId: string;
	isAbbreviatedReadTime?: boolean;
	isDetailsPanel?: boolean;
	showWordCount?: boolean;
	features: ContentFeatures | null;
	featuresSkipExpand: ContentFeatures | null;
	wordCount: number;
	readTime: number | null;
	source: string;
	isEligibleForLoomAIScriptSection?: boolean;
}) => {
	const [isPopupOpen, setIsPopupOpen] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	/**
	 * Will return `false` if undefined
	 */
	const hasDismissedLoomScriptReadTimeButtonNudge = localStorage.getItemAsBoolean(
		PERSISTED_KEYS_ON_SERVER.PERSISTED_LOOM_SCRIPT_READTIME_BUTTON_NUDGE_DISMISSED,
	);

	/**
	 * See https://hello.atlassian.net/wiki/spaces/Growth/pages/5129359003/Loom+AI+readtime+prompt+and+script+for+confluence+pages+Project+Poster
	 *
	 * If the page doesn't meet the criteria (readtime, is page creator) no point in showing the nudge
	 */
	const shouldShowLoomScriptReadTimeButtonNudge =
		isEligibleForLoomAIScriptSection && !hasDismissedLoomScriptReadTimeButtonNudge;

	const onPageReadTimeClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'readTime',
				actionSubjectId: 'readTimePopup',
				source,
				attributes: {
					contentId,
				},
			},
		}).fire();
		setIsPopupOpen(!isPopupOpen);
	}, [isPopupOpen, createAnalyticsEvent, contentId, source]);

	return (
		<Popup
			isOpen={isPopupOpen}
			onClose={() => setIsPopupOpen(false)}
			placement="bottom-start"
			content={(props) => (
				<ReadTimePopup
					{...props}
					features={features}
					featuresSkipExpand={featuresSkipExpand}
					source={source}
					isEligibleForLoomAIScriptSection={isEligibleForLoomAIScriptSection}
					contentId={contentId}
				/>
			)}
			trigger={(triggerProps) =>
				isEligibleForLoomAIScriptSection ? (
					<LoomScriptReadTimeButtonNudgeWrapper enabled={shouldShowLoomScriptReadTimeButtonNudge}>
						<Pressable {...triggerProps} onClick={onPageReadTimeClick} xcss={triggerStyles}>
							<ReadTimeComponent
								readTime={readTime}
								wordCount={wordCount}
								pageId={contentId}
								isAbbreviatedReadTime={isAbbreviatedReadTime}
								isDetailsPanel={isDetailsPanel}
								showWordCount={showWordCount}
							/>
						</Pressable>
					</LoomScriptReadTimeButtonNudgeWrapper>
				) : (
					<Pressable {...triggerProps} onClick={onPageReadTimeClick} xcss={triggerStyles}>
						<ReadTimeComponent
							readTime={readTime}
							wordCount={wordCount}
							pageId={contentId}
							isAbbreviatedReadTime={isAbbreviatedReadTime}
							isDetailsPanel={isDetailsPanel}
							showWordCount={showWordCount}
						/>
					</Pressable>
				)
			}
		/>
	);
};
